import { ArrowLeft, Notepad2, NoteRemove, Printer, ReceiptEdit, Trash } from "iconsax-reactjs";
import NewDashboardLayout from "../../../component/layout/NewDashboardLayout";
import MoreOptionsDropdown from "../../../component/flex/shipments/modals/MoreOptionsDropdown";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { shipmentStatus } from "constants/global";
import ActivityTab from "component/flex/shipments/tabs/ActivityTab";
import DeleteFlexInvoice from "component/DeleteFlexInvoice";
import { useEffect, useState } from "react";
import RecordPayment from "component/flex/RecordPayment";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
    handleDeleteSingleInvoice,
    handleDownloadInvoice,
    handleGetSingleInvoice,
    handleMakePayment,
    handleRejectSingleInvoice,
    handleSendReminder,
} from "api/fetchers";
import { useSelector } from "react-redux";
import Loading from "component/loading";
import getInitials from "utilities/getInitials";
import currencyPairCheck from "utilities/currencyPairCheck";
import { formatMoney } from "utilities/formatMoney";
import { calculatePercentagePaid, formatDate } from "component/flex/utils";
import { errorAlert, successAlert } from "utilities/alerts";
import useGetRequest from "hooks/useGetRequest";
import { toast } from "react-toastify";
import RejectInvoiceModal from "component/flex/invoice/modal/RejectModal";
import MakePayModal from "component/flex/invoice/modal/MakePayModal";
import PinModal from "component/pay/PinModal";
import TransferSuccessModal from "component/pay/TransferSuccessModal";
import { Bars } from "react-loader-spinner";
import useUserDetails from "hooks/useUserDetails";
import Swal from "sweetalert2";

const ManageInvoice = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { state } = useLocation();
    const { id: invoiceId } = useParams();
    const { business } = useSelector((state) => state);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showReject, setShowReject] = useState(false);
    const [makePaymentModal, setMakePaymentModal] = useState(false);
    const [showOtp, setShowOtp] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [recordPaymentModal, setRecordPaymentModal] = useState(false);
    const [amount, setAmount] = useState("");
    const [paymentType, setPaymentType] = useState("");
    const [fetchActivities, activities] = useGetRequest();
    const handleFetchActivities = (invoiceId) => {
        fetchActivities(`/v1/activity/${invoiceId}`, {}, false);
    };

    const {
        data: singleInvoice,
        isLoading: loadingInvoice,
        refetch,
    } = useQuery({
        queryKey: ["singleInvoice", invoiceId],
        queryFn: () =>
            business?._id &&
            handleGetSingleInvoice({ businessId: business?._id, currency: "NGN", invoiceId }),
    });
    const {
        title,
        totalAmount,
        paidAmount,
        issueDate,
        dueDate,
        paymentStatus: status,
        currency,
        orderItems,
        invoiceItems,
        invoiceTitle,
        invoiceID,
        issued,
        tax,
    } = singleInvoice ?? {};

    useEffect(() => {
        if (business?._id && invoiceId) {
            handleFetchActivities(invoiceId);
        }
    }, [business?._id, invoiceId]);

    const { walletBalance, loadingWallets, refetchWallet } = useUserDetails({ currency });

    const { mutateAsync: deleteSingleInvoice, isLoading: deletingInvoice } = useMutation({
        mutationFn: () => {
            handleDeleteSingleInvoice(invoiceId);
        },
        mutationKey: ["delete-single-invoice"],
        onSettled: async (data) => {
            await queryClient.invalidateQueries({ queryKey: ["singleInvoice", invoiceId] });
            successAlert("Invoice deleted successfully.");
            setShowDeleteModal(false);
            navigate("/invoice");
        },
    });

    const { mutateAsync: makePaymentFunc, isLoading: makingPaymentLoading } = useMutation({
        mutationFn: ({ amount, pin }) => handleMakePayment({ invoiceId, amount: String(amount), pin }),
        mutationKey: ["make-invoice-payment"],
        onSettled: async (data) => {
            if (data?.status === 400) {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: data?.response?.data?.message ||
                        data?.response?.data?.message?.[0] ||
                        data?.response?.data?.message[0] ||
                        "Something went wrong while making payment",
                });
            } else {
                toast.success(data?.message || "Payment made successfully");
                refetchWallet();
                refetch();
                handleFetchActivities(invoiceId);
                queryClient.invalidateQueries({
                    queryKey: ["singleInvoice", invoiceId, "user-wallet", currency],
                });
                setShowOtp(false);
                setAmount("");
            }
        },
    });

    const { mutateAsync: rejectInvoice, isLoading: rejectingInvoice } = useMutation({
        mutationFn: () => handleRejectSingleInvoice(invoiceId),
        mutationKey: ["reject-single-invoice", invoiceId],
        onSettled: async (data) => {
            toast.success(data?.message || "Invoice rejected successfully.");
            setShowReject(false);
            await queryClient.invalidateQueries({
                queryKey: ["singleInvoice", invoiceId],
            });
        },
        onError: (data) => {
            toast.success(data?.message || "Something went wrong");
            setShowReject(false);
        },
    });

    const { mutateAsync: downloadInvoiceFunc, isLoading: downloadingInvoice } = useMutation({
        mutationKey: ["duplicateInvoice", business?._id],
        mutationFn: async (invoiceId) => {
            await handleDownloadInvoice(invoiceId);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ["alInvoice", business?._id] });
        },
        onSuccess: async () => {
            toast.success("Receipt Downloaded");
        },
        onError: async () => {
            toast.error("Failed to download receipt.");
        },
    });

    const { mutateAsync: handleSendReminderFunc, isLoading: loadingHandleSendReminder } = useMutation({
        mutationKey: ["sendReceipt", business?._id],
        mutationFn: (invoiceId) => handleSendReminder(invoiceId),
        onSettled: async (data) => {
            toast.success(data?.message)
            await queryClient.invalidateQueries({ queryKey: ["alInvoice", business?._id] });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["alInvoice", business?._id] });
        },
        onError: async () => {
            toast.error("Failed to download receipt.");
        },
    });

    const handleEditInvoice = (invoice) => {
        if (invoice.status !== "PAID") {
            navigate("/invoice/create-invoice?invoiceIdToEdit=" + invoice?._id, {
                state: { invoice, isEdit: true },
            });
        } else {
            errorAlert("You cannot edit a paid invoice");
        }
    };

    const isReceiverDetails = singleInvoice?.inbound;
    const isSenderDetails = singleInvoice?.outbound;

    const isCurrentReceiver = isReceiverDetails?.businessId === business?._id;

    const invoiceDropDownOptions = (invoice) => {
        return isCurrentReceiver
            ? [
                {
                    label: "Duplicate invoice",
                    value: "duplicate-invoice",
                    icon: <Notepad2 />,
                    action: async () => {
                        navigate("/invoice/create-invoice?invoiceIdToEdit=" + invoice?._id, {
                            state: { invoice, isDuplicating: true },
                        });
                    },
                },
                {
                    label: "Generate PDF",
                    value: "generate-PDF",
                    icon: <Printer />,
                    action: () => {
                        downloadInvoiceFunc(invoiceId);
                    },
                },
                {
                    label: "Reject invoice",
                    value: "delete-draft",
                    className:
                        invoice?.paymentStatus === "DRAFT" || invoice?.paymentStatus === "UNPAID"
                            ? "block text-red-500"
                            : "hidden",
                    icon: <Trash />,
                    action: () => {
                        setShowReject(true);
                    },
                },
            ]
            : [
                {
                    label: "Edit invoice",
                    value: "edit-invoice",
                    className:
                        invoice?.paymentStatus === "UNPAID" || invoice?.paymentStatus === "DRAFT"
                            ? ""
                            : "hidden",
                    icon: <ReceiptEdit />,
                    action: () => {
                        handleEditInvoice(invoice);
                    },
                },
                {
                    label: "Duplicate invoice",
                    value: "duplicate-invoice",
                    icon: <Notepad2 />,
                    action: async () => {
                        navigate("/invoice/create-invoice?invoiceIdToEdit=" + invoice?._id, {
                            state: { invoice, isDuplicating: true },
                        });
                    },
                },
                {
                    label: "Generate PDF",
                    value: "generate-PDF",
                    icon: <Printer />,
                    action: () => {
                        downloadInvoiceFunc(invoiceId);
                    },
                },
                {
                    label: "Record payment",
                    value: "record-payment",
                    className:
                        invoice?.paymentStatus === "DRAFT" || invoice?.paymentStatus === "PAID"
                            ? "hidden"
                            : "block",
                    icon: <NoteRemove />,
                    action: () => {
                        setSelectedInvoice(invoice);
                        setRecordPaymentModal(true);
                    },
                },
                {
                    label: "Delete invoice",
                    value: "delete-draft",
                    className:
                        invoice?.paymentStatus === "DRAFT" || !invoice?.paidAmount
                            ? "block text-red-500"
                            : "hidden",
                    icon: <Trash />,
                    action: () => {
                        setShowDeleteModal(true);
                    },
                },
            ];
    };

    const invoiceItemsList = invoiceItems?.length ? invoiceItems : orderItems;
    const mainTitle = title ? title : invoiceTitle || invoiceID;

    return (
        <NewDashboardLayout>
            {(loadingInvoice || downloadingInvoice || loadingWallets) && (
                <div className="fixed left-0 top-0 h-screen w-screen z-[1000000] opacity-65">
                    <Loading />
                </div>
            )}
            <main className="py-2 mx-auto">
                <button
                    onClick={() => {
                        navigate(-1);
                    }}
                    className="flex items-center bg-transparent border-none outline-none !text-secondary gap-x-2"
                >
                    <ArrowLeft className="" /> Back
                </button>
                <section className="mt-2">
                    <div className="flex flex-wrap items-center justify-between gap-3 mt-2 md:flex-nowrap">
                        <div className="flex items-center gap-x-2 ">
                            <h1 className="text-2xl m-0 font-[450] text-main-dark">{mainTitle}</h1>
                            <span
                                style={{
                                    backgroundColor: shipmentStatus[status]?.bgColor || "#DBDFE5",
                                    color: shipmentStatus[status]?.textColor || "#B64607",
                                }}
                                className={`rounded-xl py-1 px-2 w-auto text-xs font-medium`}
                            >
                                {shipmentStatus[status]?.text}
                            </span>
                        </div>

                        {!isCurrentReceiver ? (
                            <>
                                {status === "DRAFT" && (
                                    <div className="flex items-center gap-10">
                                        <button
                                            onClick={() => handleEditInvoice(singleInvoice)}
                                            disabled={status === "PAID"}
                                            className="flex items-center bg-transparent border-none outline-none !text-secondary gap-x-2"
                                        >
                                            Edit draft
                                        </button>

                                        <MoreOptionsDropdown
                                            options={invoiceDropDownOptions(singleInvoice)}
                                            horizontal
                                        />
                                    </div>
                                )}
                                {status === "UNPAID" && (
                                    <div className="flex items-center gap-10">
                                        <button
                                            onClick={() => {
                                                setSelectedInvoice(singleInvoice);
                                                setRecordPaymentModal(true);
                                            }}
                                            className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-3 md:!px-5 text-sm`}
                                        >
                                            Record payment
                                        </button>

                                        <MoreOptionsDropdown
                                            options={invoiceDropDownOptions(singleInvoice)}
                                            horizontal
                                        />
                                    </div>
                                )}
                                {status === "DUE" && (
                                    <div className="flex items-center gap-6">
                                        <button
                                            disable={loadingHandleSendReminder}
                                            onClick={() =>
                                                handleSendReminderFunc(singleInvoice?._id)
                                            }
                                            className={`hover:opacity-90 !bg-secondary-10 mt-0 sm:!mt-0 flex items-center gap-2 !text-secondary rounded-lg py-[10px] px-4 text-sm`}
                                        >
                                            {loadingHandleSendReminder ? <Bars height="20" width="20" color="#fff" ariaLabel="bars-loading" /> : "Send reminder"}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedInvoice(singleInvoice);
                                                setRecordPaymentModal(true);
                                            }}
                                            className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-4 text-sm`}
                                        >
                                            Record payment
                                        </button>

                                        <MoreOptionsDropdown
                                            options={invoiceDropDownOptions(singleInvoice)}
                                            horizontal
                                        />
                                    </div>
                                )}
                                {status === "PAID" && (
                                    <div className="flex items-center gap-10">
                                        <button
                                            className={`hover:opacity-90 !bg-secondary mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-4 text-sm`}
                                        >
                                            {loadingHandleSendReminder ? <Bars height="20" width="20" color="#fff" ariaLabel="bars-loading" /> : "Send receipt"}
                                        </button>

                                        <MoreOptionsDropdown
                                            options={invoiceDropDownOptions(singleInvoice)}
                                            horizontal
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {status === "REJECTED" ? null : status === "REJCTED" ? null : (
                                    <div className="flex items-center gap-10">
                                        {status !== "PAID" && (
                                            <button
                                                onClick={() => {
                                                    setSelectedInvoice(singleInvoice);
                                                    setMakePaymentModal(true);
                                                }}
                                                className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-3 md:!px-5 text-sm`}
                                            >
                                                Make payment
                                            </button>
                                        )}

                                        <MoreOptionsDropdown
                                            options={invoiceDropDownOptions(singleInvoice)}
                                            horizontal
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <section className="flex flex-col gap-6 md:flex-row">
                        <div className="w-full md:w-7/12">
                            <>
                                <p className="pb-1 mt-5 text-sm font-normal ">
                                    {isCurrentReceiver ? "Sent from" : "Billed to"}
                                </p>
                                <div className="mb-6 bg-white rounded-xl">
                                    <div className="flex justify-between items-center !px-5 !py-4">
                                        <div className="flex items-center justify-start gap-3">
                                            <div className="flex items-center justify-center w-10 h-10 font-semibold text-[#475467] bg-[#EFF0FB] rounded-full">
                                                {isCurrentReceiver
                                                    ? getInitials(isSenderDetails?.businessName)
                                                    : getInitials(isReceiverDetails?.businessName)}
                                            </div>
                                            <div className="text-left">
                                                <h3 className="text-lg font-medium mb-0 !text-left capitalize text-gray-60">
                                                    {isCurrentReceiver
                                                        ? isSenderDetails?.businessName
                                                        : isReceiverDetails?.businessName}
                                                </h3>
                                                <p
                                                    style={{ letterSpacing: "-3.5%" }}
                                                    className="text-xs font-light text-[#667085]"
                                                >
                                                    {isCurrentReceiver
                                                        ? isSenderDetails?.email
                                                        : isReceiverDetails?.businessName || "N/A"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>

                            <>
                                <p className="pb-1 mt-5 text-sm font-normal">Invoice Details</p>
                                <div className="p-3 mb-6 bg-white rounded-xl">
                                    <div className="mb-3">
                                        <p className="text-sm font-light text-secondary-40">
                                            Amount due
                                        </p>
                                        <p className="text-2xl font-normal text-gray-90">
                                            {currencyPairCheck(currency)}
                                            {formatMoney(totalAmount - paidAmount)}
                                        </p>
                                    </div>

                                    <div className="flex items-start justify-between">
                                        <div className="w-1/2">
                                            <p className="text-sm font-light text-secondary-40">
                                                Issued
                                            </p>
                                            <p className="text-sm font-normal text-gray-90">
                                                {formatDate(issued || issueDate, "MMM D, YYYY")}
                                            </p>
                                        </div>
                                        <div className="w-1/2">
                                            <p className="text-sm font-light text-secondary-40">
                                                Due
                                            </p>
                                            <p className="text-sm font-normal text-gray-90">
                                                {formatDate(dueDate, "MMM D, YYYY")}
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        style={{ border: "1px solid" }}
                                        className="mt-3 rounded-lg !border-light-grey"
                                    >
                                        {true && (
                                            <div className="!p-5 border-b border-light-grey">
                                                <p className="text-sm flex text-[#475467]">
                                                    <span className="font-normal text-gray-70">
                                                        Payment progress
                                                    </span>
                                                    <span className="ml-auto font-light text-secondary-40">
                                                        {calculatePercentagePaid(
                                                            totalAmount,
                                                            paidAmount
                                                        )}
                                                        % complete
                                                    </span>
                                                </p>
                                                <div className="bg-[#EAECF0] rounded-[20px] h-2 !my-3.5 relative">
                                                    <div
                                                        className="bg-[#11A811] absolute left-0 top-0 h-full rounded-[20px] transition-[width]"
                                                        style={{
                                                            width: `${calculatePercentagePaid(
                                                                totalAmount,
                                                                paidAmount
                                                            )}%`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            className="flex justify-between gap-2 !p-5 !border-light-grey"
                                            style={{ borderTop: "1px solid" }}
                                        >
                                            <div className="flex items-start justify-between w-full">
                                                <div className="w-1/2">
                                                    <p className="text-sm font-light text-secondary-40">
                                                        Total
                                                    </p>
                                                    <p className="text-sm font-normal text-gray-90">
                                                        {currencyPairCheck(currency)}
                                                        {formatMoney(totalAmount)}{" "}
                                                        {tax && (
                                                            <span className="text-xs font-light text-secondary-40">
                                                                (Incl. VAT)
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div className="w-1/2">
                                                    <p className="text-sm font-light text-secondary-40">
                                                        Paid
                                                    </p>
                                                    <p className="text-sm font-normal text-gray-90">
                                                        {currencyPairCheck(currency)}
                                                        {formatMoney(paidAmount)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>

                            <>
                                <p className="pb-1 mt-5 text-sm font-normal">Invoice Items</p>
                                <div className="p-3 py-4 mb-6 bg-white rounded-xl">
                                    {invoiceItemsList?.length ? (
                                        invoiceItemsList?.map(
                                            ({ itemName, pricePerUnit, quantity }, index) => (
                                                <div
                                                    key={index}
                                                    style={{ borderBottom: "1px solid #EBEDF9" }}
                                                    className="flex items-center justify-between py-3 "
                                                >
                                                    <div>
                                                        <p className="text-sm text-gray-90">
                                                            {itemName}
                                                        </p>
                                                        <p className="text-xs text-secondary-70">
                                                            {currencyPairCheck(currency)}
                                                            {formatMoney(pricePerUnit)}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-90">
                                                            {currencyPairCheck(currency)}
                                                            {formatMoney(pricePerUnit * quantity)}
                                                        </p>
                                                        <p className="text-xs text-secondary-70">
                                                            {quantity} units
                                                        </p>
                                                    </div>
                                                </div>
                                            )
                                        )
                                    ) : (
                                        <p className="text-base text-center text-gray-90">
                                            No items in this invoice
                                        </p>
                                    )}
                                </div>
                            </>
                        </div>

                        <div className="w-full md:w-5/12">
                            <>
                                <p className="pb-1 mt-5 text-sm font-normal">Activity log</p>
                                <div className="p-3 py-4 mb-6 bg-white rounded-xl">
                                    <ActivityTab activity={activities} />
                                </div>
                            </>
                        </div>
                    </section>
                </section>
            </main>
            <DeleteFlexInvoice
                show={showDeleteModal}
                setshow={setShowDeleteModal}
                loader={deletingInvoice}
                deletedBuyer={() => {
                    deleteSingleInvoice();
                }}
            />
            {recordPaymentModal && (
                <RecordPayment
                    order={selectedInvoice}
                    orderId={state?.id}
                    totalAmount={selectedInvoice?.amount}
                    close={() => setRecordPaymentModal(false)}
                    type="invoice"
                    refetchActivity={() => {
                        handleFetchActivities(invoiceId);
                        refetch();
                        queryClient.invalidateQueries({ queryKey: ["singleInvoice", invoiceId] });
                        setRecordPaymentModal(false);
                    }}
                />
            )}
            <RejectInvoiceModal
                show={showReject}
                setShow={setShowReject}
                onClick={() => {
                    rejectInvoice();
                }}
                isLoading={rejectingInvoice}
            />
            {makePaymentModal && (
                <MakePayModal
                    currency={currency}
                    walletBalance={walletBalance}
                    onClick={() => {
                        if (amount > walletBalance) {
                            return toast.error("Amount can not be greater than wallet balance");
                        } else {
                            setMakePaymentModal(false);
                            setShowOtp(true);
                        }
                    }}
                    maxAmount={totalAmount - paidAmount}
                    isLoading={makingPaymentLoading}
                    isRepayment={true}
                    showFullAmountToggle={false}
                    paymentType={paymentType}
                    setPaymentType={setPaymentType}
                    show={makePaymentModal}
                    setShow={setMakePaymentModal}
                    amount={amount}
                    setAmount={setAmount}
                />
            )}
            <PinModal
                isInvoice
                isOpen={showOtp}
                goBack={() => {
                    setMakePaymentModal(true);
                    setShowOtp(false);
                }}
                onClose={() => {
                    setShowOtp(false);
                }}
                onConfirm={(data) => {
                    makePaymentFunc({ amount, pin: data });
                }}
            />
            <TransferSuccessModal />
        </NewDashboardLayout>
    );
};

export default ManageInvoice;
